import { z } from 'zod';

export const PolicyDomainSchema = z.enum([
  'doctrine',
  'setup',
  'portfolio',
  'freshness',
  'learning',
  'decision',
]);

export type PolicyDomain = z.infer<typeof PolicyDomainSchema>;

export const PolicyVersionSchema = z.object({
  policyVersionId: z.string(),
  domain: PolicyDomainSchema,
  version: z.string(),
  effectiveAt: z.string(),
  knownAt: z.string(),
  changelog: z.string(),
  contentHash: z.string(),
  sourceIds: z.array(z.string()).min(1),
  supersededBy: z.string().optional(),
});

export type PolicyVersion = z.infer<typeof PolicyVersionSchema>;

/**
 * Policy promotion lifecycle (Section 16.5):
 * PROPOSED → replay → holdout → shadow → canary → human APPLY → rollbackable.
 * No automated path may reach APPLIED.
 */
export const PolicyProposalStatusSchema = z.enum([
  'PROPOSED',
  'REPLAY_PASSED',
  'HOLDOUT_PASSED',
  'SHADOW',
  'CANARY',
  'APPLIED',
  'REJECTED',
  'ROLLED_BACK',
]);

export type PolicyProposalStatus = z.infer<typeof PolicyProposalStatusSchema>;

export const PolicyProposalSchema = z.object({
  proposalId: z.string(),
  domain: PolicyDomainSchema,
  title: z.string(),
  rationale: z.string(),
  proposedChanges: z.record(z.unknown()),
  status: PolicyProposalStatusSchema,
  /** Promotion without a valid replay hash must fail (Task 17). */
  replayHash: z.string().optional(),
  reportHash: z.string().optional(),
  proposedBy: z.enum(['human', 'model_draft']),
  humanApprovedBy: z.string().optional(),
  humanApprovedAt: z.string().optional(),
  sampleSize: z.number().int().nonnegative().default(0),
  uncertaintyNote: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  sourceIds: z.array(z.string()).min(1),
});

export type PolicyProposal = z.infer<typeof PolicyProposalSchema>;

export function canTransitionProposal(
  from: PolicyProposalStatus,
  to: PolicyProposalStatus,
): boolean {
  const allowed: Record<PolicyProposalStatus, PolicyProposalStatus[]> = {
    PROPOSED: ['REPLAY_PASSED', 'REJECTED'],
    REPLAY_PASSED: ['HOLDOUT_PASSED', 'REJECTED'],
    HOLDOUT_PASSED: ['SHADOW', 'REJECTED'],
    SHADOW: ['CANARY', 'REJECTED'],
    CANARY: ['APPLIED', 'REJECTED'],
    APPLIED: ['ROLLED_BACK'],
    REJECTED: [],
    ROLLED_BACK: [],
  };
  return allowed[from].includes(to);
}

/**
 * Hard rule (Section 16.5): doctrine and capital-survival rules may never be
 * mutated automatically; APPLY always requires human approval.
 */
export function validateProposalApply(
  proposal: PolicyProposal,
  approver: string | undefined,
): string[] {
  const errors: string[] = [];
  if (proposal.status !== 'CANARY') {
    errors.push('Proposal must complete CANARY before APPLY');
  }
  if (!proposal.replayHash) {
    errors.push('Promotion without a valid replay hash fails');
  }
  if (!proposal.reportHash) {
    errors.push('Promotion without a replay report hash fails');
  }
  if (!approver) {
    errors.push('APPLY requires explicit human approval');
  }
  return errors;
}
